import { SalesOrderContractTask, SalesOrderContractTaskPhase } from '@/hooks/api/models';
import { useLoading } from '@/providers/LoadingProvider';
import { useBackofficeSearchCondition } from '@/providers/BackofficeSearchConditionProvider';
import { useSalesOrderContractTaskPhases } from '@/hooks/api/1610_sales_order_contract_task_phases';
import { useSalesOrderContractTasks } from '@/hooks/api/1620_sales_order_contract_tasks';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useMoveSalesOrderContractTaskPhaseToNext } from '@/hooks/api/1670_move_to_next_phase';
import { useMoveSalesOrderContractTaskPhaseToPrev } from '@/hooks/api/1671_move_to_prev_phase';
import { useSalesOrderContractTaskInvalidate } from '@/hooks/api/1660_sales_order_contract_task';
import { toastApiError } from '@/lib/toast';
import { Link, useNavigate } from 'react-router-dom';
import { SalesOrderLogic } from '@/hooks/api/models/logics';
import { dateFormat } from '@/utils';

type PhaseTasks = {
  phase: SalesOrderContractTaskPhase;
  tasks: SalesOrderContractTask[];
};

export const KanbanElement = () => {
  const { setShowLoading } = useLoading();
  const { salesOrderContractTaskSearchCondition: searchCondition } = useBackofficeSearchCondition();
  const { data: phases } = useSalesOrderContractTaskPhases();
  const {
    data: tasksRes,
    isPending,
    refetch,
  } = useSalesOrderContractTasks({ ...searchCondition, sales_order_contract_task_phase_ids: [], per: 1000 });
  const [phaseTasksList, setPhaseTasksList] = useState<PhaseTasks[]>([]);
  const [updatedTask, setUpdatedTask] = useState<SalesOrderContractTask>();

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    refetch();
  }, [searchCondition]);

  useEffect(() => {
    if (updatedTask) {
      const _phaseTasksList: PhaseTasks[] = [...phaseTasksList];
      for (const _phaseTask of _phaseTasksList) {
        _phaseTask.tasks = _phaseTask.tasks.filter((d) => d.id != updatedTask.id);

        if (_phaseTask.phase.id == updatedTask.sales_order_contract_task_phase.id) {
          _phaseTask.tasks = [..._phaseTask.tasks, updatedTask];
        }
      }
      setPhaseTasksList((_) => _phaseTasksList);
      refetch();
    }
  }, [updatedTask]);

  useEffect(() => {
    if (phases && tasksRes) {
      const _phaseTasksList: PhaseTasks[] = [];
      for (const phase of phases) {
        const tasks = tasksRes.sales_order_contract_tasks.filter(
          (d) => d.sales_order_contract_task_phase.id == phase.id
        );
        _phaseTasksList.push({ phase, tasks });
      }
      setPhaseTasksList((_) => _phaseTasksList);
    }
  }, [phases, tasksRes]);

  if (phases == null || tasksRes == null) return <></>;

  return (
    <div className="mb-4 kanban-container">
      <div className="py-2 d-inline-flex" style={{ overflowX: 'auto' }}>
        <div id="myKanban">
          <div className="kanban-container" style={{ width: 1240 }}>
            {phaseTasksList.map((d) => (
              <PhaseTasksElement
                key={`p-t-${d.phase.id}`}
                phases={phases}
                phaseTasks={d}
                setUpdatedTask={setUpdatedTask}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

type PhaseTasksProps = {
  phases: SalesOrderContractTaskPhase[];
  phaseTasks: PhaseTasks;
  setUpdatedTask: Dispatch<SetStateAction<SalesOrderContractTask | undefined>>;
};
const PhaseTasksElement = ({ phases, phaseTasks, setUpdatedTask }: PhaseTasksProps) => {
  return (
    <div
      data-id="_backlog"
      data-order="1"
      className="kanban-board"
      style={{ width: 300, marginLeft: 10, marginTop: 10 }}
    >
      <header className="kanban-board-header">
        <div className="kanban-title-board">{phaseTasks.phase.label}</div>
      </header>
      <main className="kanban-drag">
        {phaseTasks.tasks.map((d) => (
          <TaskElement key={`t-${d.id}`} phases={phases} task={d} setUpdatedTask={setUpdatedTask} />
        ))}
      </main>
      <footer></footer>
    </div>
  );
};

type TaskProps = {
  phases: SalesOrderContractTaskPhase[];
  task: SalesOrderContractTask;
  setUpdatedTask: Dispatch<SetStateAction<SalesOrderContractTask | undefined>>;
};

const TaskElement = ({ phases, task, setUpdatedTask }: TaskProps) => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();
  const {
    mutate: mutateForNext,
    data: dataForNext,
    isPending: isPendingForNext,
    error: errorForNext,
  } = useMoveSalesOrderContractTaskPhaseToNext();
  const {
    mutate: mutateForPrev,
    data: dataForPrev,
    isPending: isPendingForPrev,
    error: errorForPrev,
  } = useMoveSalesOrderContractTaskPhaseToPrev();
  const { invalidate } = useSalesOrderContractTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: task.sales_order.team_outline.id,
    sales_order_id: task.sales_order.id,
    id: task.id,
  });

  useEffect(() => setShowLoading(isPendingForNext), [isPendingForNext]);
  useEffect(() => setShowLoading(isPendingForPrev), [isPendingForPrev]);
  useEffect(() => toastApiError(errorForNext), [errorForNext]);
  useEffect(() => toastApiError(errorForPrev), [errorForPrev]);

  useEffect(() => {
    if (dataForNext) {
      setUpdatedTask(dataForNext);
      invalidate();
    }
  }, [dataForNext]);

  useEffect(() => {
    if (dataForPrev) {
      setUpdatedTask(dataForPrev);
      invalidate();
    }
  }, [dataForPrev]);

  const onMoveNextClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    setShowLoading(true);
    mutateForNext({
      identifier: _w_!.identifier,
      team_id: task.sales_order.team_outline.id,
      sales_order_id: task.sales_order.id,
      task_id: task.id,
      current_sales_order_contract_task_phase_id: task.sales_order_contract_task_phase.id,
    });
  };

  const onMovePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    setShowLoading(true);
    mutateForPrev({
      identifier: _w_!.identifier,
      team_id: task.sales_order.team_outline.id,
      sales_order_id: task.sales_order.id,
      task_id: task.id,
      current_sales_order_contract_task_phase_id: task.sales_order_contract_task_phase.id,
    });
  };

  return (
    <div
      className="kanban-item border-radius-md"
      onClick={() =>
        navigate(
          `/w/${_w_!.identifier}/backoffice/teams/${task.sales_order.team_outline.id}/sales_orders/${
            task.sales_order.id
          }/sales_order_contract_tasks/${task.id}`
        )
      }
    >
      {task.alert && (
        <div className="mb-2">
          <span className="badge badge-xs badge-danger">{task.alert}</span>
        </div>
      )}

      <div className="d-flex align-items-center">
        <h6 className="text-truncate mb-0 flex-1 text-sm me-1">{task.name}</h6>

        {phases[0].id != task.sales_order_contract_task_phase.id && (
          <div className="d-flex">
            <button className="btn btn-icon-only btn-sm bg-gradient-warning me-1" onClick={onMovePrevClick}>
              <i className="fas fa-arrow-left" aria-hidden="true"></i>
            </button>
          </div>
        )}
        {phases.at(-1)!.id != task.sales_order_contract_task_phase.id && (
          <button className="btn btn-icon-only btn-sm bg-gradient-success" onClick={onMoveNextClick}>
            <i className="fas fa-arrow-right" aria-hidden="true"></i>
          </button>
        )}
      </div>

      <div className="avatar-group mb-2">
        <img src={task.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />
        <span className="text-xs text-dark text-truncate">{task.user.user_account.name}</span>
        {/*{task.sales_order_contract_task_subscriptions.map((d) => (*/}
        {/*  <img key={`t-subsctiption-${d.id}`} src={d.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />*/}
        {/*))}*/}
      </div>
      <div className="d-flex flex-wrap">
        <span className="badge badge-xs bg-gradient-info me-1 mb-1 text-truncate">{task.sales_order.name}</span>
        <span className="badge badge-xs bg-gradient-primary me-1 mb-1 text-truncate">
          {task.sales_order.business_partner.display_name}
        </span>
        <span className="badge badge-xs bg-gradient-dark me-1 mb-1 text-truncate">
          {task.sales_order.team_outline.name}
        </span>
        <span className="badge badge-xs bg-gradient-dark me-1 mb-1 text-truncate">{task.contract_context.label}</span>
        <span className="badge badge-xs bg-gradient-success me-1 mb-1 text-truncate">
          ¥{task.sales_order.accounting_total_amount.toLocaleString()}
        </span>
      </div>
      <hr className="horizontal dark my-2" />
      <div className="d-flex justify-content-between">
        <div>
          <div className="text-secondary text-xxs">期限日</div>
          <div className="font-weight-bold text-dark text-xs">
            {task.due_date ? dateFormat({ date: task.due_date }) : '-'}
          </div>
        </div>
        <div className="text-end">
          {/*<div className="text-secondary text-xxs">Assigned To</div>*/}
          {/*<div className="font-weight-bold text-dark text-xs">{task.user.user_account.name}</div>*/}
        </div>
      </div>
    </div>
  );
};
